/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { getCookie } from '@/cart/lib/cookies';
import { StoreConfigModel } from '@/cart/data/models/store-models';
import { getAuthenticatedCart, setAuthenticatedCart } from './persisted-data';

type State = {
  state: {
    displayMiniCart: boolean;
    miniCartMaxItemsDisplay: number;
    cartExpiresInDays: number;
    cartSummaryDisplayTotal: number;
    defaultCountry: string;
    categoryFixedProductTaxDisplaySetting: string;
    productFixedProductTaxDisplaySetting: string;
    salesFixedProductTaxDisplaySetting: string;
    useConfigurableParentThumbnail: boolean;
    shoppingCartDisplaySetting: {
      zeroTax: boolean;
      subtotal: string;
      price: string;
      shipping: string;
      fullSummary: boolean;
      grandTotal: boolean;
      taxGiftWrapping: string;
    };
  };
  cartId: string | null;
  authenticated: boolean;
  initializing?: boolean;
  locale?: string;
  config?: StoreConfigModel | null;
};

const _state: State = (() => {
  const isAuthenticated = getAuthenticatedCart();
  return {
    cartId: null,
    authenticated: isAuthenticated,
  };
})();

// Proxy state to allow reactivity
export const state = new Proxy(_state, {
  set(target, key, value) {
    // @ts-ignore
    target[key] = value;

    if (key === 'cartId') {
      // only update cookie if value has changed
      if (value === state.cartId) return true;

      if (value === null) {
        // remove cookie
        document.cookie = `DROPIN__CART__CART-ID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
        return true;
      }

      // get expiration days from config
      const expiresInDays = state.config?.cartExpiresInDays;

      if (!expiresInDays) {
        console.warn(
          'Missing "expiresInDays" config. Cookie expiration will default to 30 days.'
        );
      }

      // set expiration date
      const expires = new Date();
      expires.setDate(expires.getDate() + (expiresInDays ?? 30));

      // set cookie
      document.cookie = `DROPIN__CART__CART-ID=${value}; expires=${expires.toUTCString()}; path=/`;
    }

    if (key === 'authenticated') {
      setAuthenticatedCart(value);
    }

    return true;
  },
  get(target, key) {
    if (key === 'cartId') {
      // get value from cookie
      return getCookie('DROPIN__CART__CART-ID');
    }

    return target[key as keyof State];
  },
});
